import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import api, { getRequest } from "../apis/ApiInstence";
import LightGallery from "lightgallery/react";
import lgZoom from "lightgallery/plugins/zoom";
import lgVideo from "lightgallery/plugins/video";
import lgThumbnail from "lightgallery/plugins/thumbnail";

import "lightgallery/css/lightgallery.css";
import "lightgallery/css/lg-thumbnail.css";
import "lightgallery/css/lg-zoom.css";
import "lightgallery/css/lg-video.css";
import "./Gallery.css";



const Gallery = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const lgInstanceRef = useRef(null);

  const assetBase = useMemo(() => {
    const base = String(api?.defaults?.baseURL || "").replace(/\/$/, "");
    return base.replace(/\/api\/v1$/, "");
  }, []);

  const lightGalleryKey = useMemo(
    () => process.env.REACT_APP_LIGHTGALLERY_LICENSE_KEY || "",
    []
  );

  const resolveImageUrl = useCallback((imagePath) => {
    const raw = String(imagePath || "").trim();
    if (!raw) return "";
    if (/^https?:\/\//i.test(raw)) return raw;

    const normalized = raw.replace(/^\/+/, "");
    if (normalized.startsWith("gallery/")) return `${assetBase}/${normalized}`;
    return `${assetBase}/gallery/${normalized}`;
  }, [assetBase]);

  const dynamicEl = useMemo(
    () =>
      items
        .map((item) => {
          const src = resolveImageUrl(item.image);
          if (!src) return null;
          return { src, thumb: src };
        })
        .filter(Boolean),
    [items, resolveImageUrl]
  );

  useEffect(() => {
    const controller = new AbortController();
    let mounted = true;

    const fetchGallery = async () => {
      setLoading(true);
      setError("");

      try {
        const token = localStorage.getItem("token");
        const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
        const res = await getRequest("/galleries", {
          signal: controller.signal,
          ...(headers ? { headers } : {}),
        });
        const payload = res?.data;
        const list = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.data)
            ? payload.data
            : Array.isArray(payload?.data?.data)
              ? payload.data.data
              : [];
        const normalized = list
          .filter(Boolean)
          .map((row) => ({
            id: row.id ?? row._id ?? `${row.image || ""}-${Math.random()}`,
            name: row.name ?? "",
            image: row.image ?? row.image_url ?? row.imageUrl ?? "",
          }))
          .filter((row) => row.image);

        if (!mounted) return;
        setItems(normalized);
      } catch (err) {
        if (!mounted) return;
        if (err?.name === "CanceledError") return;
        setError("Gallery couldn\u2019t be loaded. Please try again.");
        setItems([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchGallery();

    return () => {
      mounted = false;
      controller.abort();
    };
  }, []);

  return (
    <div className="overflow-hidden bg-[#f8faff]">
      {/* ================= BREADCRUMB ================= */}
      <div className="max-w-7xl mx-auto px-4 py-5">
        <div className="grid grid-cols-1 sm:grid-cols-3 items-center gap-3">
          <div className="flex items-center gap-3 text-gray-500 text-sm">
            <button
              onClick={() => navigate(-1)}
              className="w-9 h-9 flex items-center justify-center rounded-full border hover:bg-gray-100 transition text-gray-700"
              aria-label="Go back"
              type="button"
            >
              ←
            </button>
            <span>
              <button onClick={() => navigate("/")} className="hover:underline" type="button">
                Home
              </button>{" "}
              / <span className="text-black font-medium">Gallery</span>
            </span>
          </div>

          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#351F72] text-center">
            Our Gallery
          </h1>

          <div className="hidden sm:block" />
        </div>
      </div>

      {/* ================= GALLERY GRID ================= */}
      <section>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="pc-gallery-surface">
            {loading && (
              <div className="py-10 text-center text-gray-600 font-medium">Loading gallery...</div>
            )}
            {error && (
              <div className="py-6 text-center text-red-600 font-medium">{error}</div>
            )}
            {!loading && !error && items.length === 0 && (
              <div className="py-10 text-center text-gray-600 font-medium">
                No images found in gallery.
              </div>
            )}

            <LightGallery
              key={dynamicEl.length}
              licenseKey={lightGalleryKey || undefined}
              plugins={[lgZoom, lgVideo, lgThumbnail]}
              mode="lg-fade"
              pager={false}
              thumbnail={true}
              galleryId={"nature"}
              autoplayFirstVideo={false}
              download={false}
              dynamic
              dynamicEl={dynamicEl}
              onInit={(detail) => {
                lgInstanceRef.current = detail?.instance || null;
              }}
              mobileSettings={{
                controls: false,
                showCloseIcon: false,
                download: false,
                rotate: false,
              }}
            />

            {!loading && !error && items.length > 0 && (
              <div className="gallery" aria-label="Gallery images">
                {items.map((item, index) => {
                  const src = resolveImageUrl(item.image);
                  const label = item.name ? item.name : `Open image ${index + 1}`;

                  return (
                    <button
                      key={item.id}
                      type="button"
                      className="gallery__item"
                      aria-label={label}
                      onClick={() => lgInstanceRef.current?.openGallery?.(index)}
                    >
                      <img
                        className="img-responsive"
                        alt={item.name || "Gallery image"}
                        loading="lazy"
                        src={src}
                      />
                    </button>
                  );
                })}
              </div>
            )}

            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-6 py-3 rounded-full font-semibold text-white bg-gradient-to-r from-[#351F72] to-[#4b2aa0] shadow-lg shadow-[#351F72]/25 hover:shadow-xl hover:shadow-[#351F72]/40 transition"
              >
                Contact Now
              </Link>
              <Link
                to="/products"
                className="inline-flex items-center justify-center px-6 py-3 rounded-full font-semibold text-[#351F72] bg-white border border-[#351F72]/20 hover:bg-[#f3f6ff] transition"
              >
                Buy Products
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Gallery;
